

class Variants {
  constructor() {
    //the raw variant/product data
    this.data = [];

    //the variant data options
    this.variants = Object.create(null);

    //contains the list of registered properties for variant selectors that should be used for parsing and filtering
    this.properties = [];

    //contains the list of registered elements for variant selectors
    this.selectors = [];

    //the list of filters that have been applied to products based on variant selector choices
    this.filters = [];

    this.product = null;

    window.addEventListener('register-variant-property', this.registerVariantProperty.bind(this));
    //dispatch an event to get any active variant selectors to register their properties
    window.dispatchEvent(new Event('get-variant-properties'));
  }

  /**
   *
   * @param {*} event event.detail.property is the incoming property for a variant selector that needs to be tracked and used to filter variants
   */
  registerVariantProperty(event){
    if (this.properties.includes(event.detail.property) == false){
      this.properties.push(event.detail.property);
    }

    if (this.selectors.includes(event.detail.element) == false){
      this.selectors.push(event.detail.element);
    }
  }

  getSelector(property)
  {
    const selector = this.selectors.find(element => element.property == property);
    return selector ? selector : false
  }

  getSelectors()
  {
    return this.selectors;
  }

  setData(data) {
    if (!data || !data?.availableVariants?.length) return;

    this.data = data.availableVariants;
    this.variants = Object.create(null);

    // Create a new variant given a format and stores it in this.variants.
    this.data.forEach((element) => {
      const remainingProperties = new Set(this.properties);
      for (let parent in element.variant_data) {
        this.setVariantData(parent, element.variant_data);
        remainingProperties.delete(parent);
      }

      // A map from property names to the original key in parent_options matching that property.
      const parentOptionProps = new Map(
        Object.keys(element.parent_options).map((parent) => {
          const prop = parent.replace('Display', '');
          remainingProperties.delete(prop);
          return [prop, parent];
        }),
      );
      // Treat any remaining property that's neither in variant_data nor parent_options as a
      // "missing" parent option whose values is 'NA'. This handles cases where the shape of
      // catalog_items.data can vary from catalog item to catalog item causing the shape of
      // parent_options to vary too.
      remainingProperties.forEach((prop) => {
        parentOptionProps.set(prop, null);
      });

      parentOptionProps.forEach((originalProp, prop) => {
        const value = (originalProp === null ? null : element.parent_options[originalProp]) || 'NA';
        const propertyData = {
          [prop]: {
            code: value,
            display: value,
            thumbnail: element.thumbnailImageUrl,
            rank: 0,
          },
        };
        // For parent options other than color such as width or inseam, the URLs of the handle and
        // their respective selectors are dynamic. Therefore, both a dynamic and a static copy will
        // be saved.
        if (element.handle) {
          propertyData[prop].staticHandle = element.handle;
          propertyData[prop].dynamicHandle = element.handle;
        }
        this.setVariantData(prop, propertyData);
        element.variant_data[prop] = propertyData[prop];
      });
    });

    // Now prune any properties for which we didn't detect any values besides 'NA'.
    const naOnly = this.properties.filter(
      property => Object.keys(this.variants[property]).every(
        value => value.toUpperCase() === 'NA',
      ),
    );
    naOnly.forEach((property) => {
      delete this.variants[property];
    });
    this.properties = this.properties.filter(
      property => this.variants[property],
    );
    this.selectors = this.selectors.filter(
      element => this.variants[element.property],
    );

    const event = new CustomEvent('variants-ready');
    window.dispatchEvent(event);
  }

  setProduct(product, extraProperties){
    //using spread in both params to merge them in product.
    this.product = { ...product, ...extraProperties };
  }
  /**
   *
   * @param {*} id - product.id passed in from Shopify
   */
  setCurrentVariant(id){
    const params = new URLSearchParams(window.location.search);

    // The size param is passed through the PDP when a size is selected.
    const sizeParam = params.get('size');
    // The variant param is passed through the PLP when a size is selected.
    const variantParam = params.get('variant');
    // We should pre-select the size and condition values in the PDP when one of these two parameters is available.
    const isSizePreSelected = Boolean(variantParam || sizeParam);
    // Filter through the product id so that subsequent searches are within a smaller dataset rather than across all possible data.
    const filteredVariants = this.data.filter(({ productId }) => productId.includes(id));
    const sortedVariants = this.sortVariants(filteredVariants);

    let variant;
    // If the variant id is present, we should select the unique variant id requested.
    if (variantParam) variant = sortedVariants.find(({ variantId }) => variantId.includes(variantParam));
    // If there is no variant stored yet, we should filter by the parameter filters and finally by the best possible condition.
    if (!variant) {
      // Create the parameter filters based on the property list to only consider color, size, and condition but in the future we could have more.
      const parameterFilters = [];
      this.properties.forEach(property => {
        const value = params.get(property);
        if (value) parameterFilters.push({ property, value });
      });

      let variants = [];
      // Filter by color, size, and condition. If not found by color and size, then by color.
      while(parameterFilters.length != 0 && variants.length == 0){
        variants = sortedVariants.filter(({ variant_data }) =>
          parameterFilters.every(({ property, value }) => {
            if (property === "color") {
              return this.hyphenate(variant_data[property].display) === value;
            }
            return variant_data[property].code === value
          }
        ));

        if (variants.length){
          variant = variants[0];
          break;
        }
        parameterFilters.pop();
      }
    }
    // If there is no variant stored yet, we should filter the first product id coincidence.
    if (!variant) variant = sortedVariants.find(({ productId }) => productId.includes(id));

    // Create a list of all properties from those subscribing to the 'register-variant-property' event and sets the default value to false unless the variant is found.
    const propertyList = Object.create(null);
    this.properties.forEach((property) => {
      propertyList[property] = variant ? variant.variant_data[property].code : false;
    });

    // Set the value of those subscribed to the events of this class, provided that the value of the selector is not false.
    for (let property in propertyList){
      if (propertyList[property] && this.hasVariantData(property, propertyList[property])){
        // It will not select size nor condition if size was not pre-selected previously.
        if ((property === 'size' || property === 'condition') && !isSizePreSelected) continue;
        this.getSelector(property).setValue(propertyList[property]);
        this.setVariantOption(property, propertyList[property], false);
      }
    }
    window.dispatchEvent(new CustomEvent('price-update'));
  }

  getCurrentVariant() {
    if (this.filters.length != this.selectors.length) return null;
    const [currentVariant] = this.getAvailableVariants(this.filters);
    return currentVariant;
  }

  getVariantData(property, filters = false, enabledOnly = false) {
    if (filters == false){
      filters = this.filters;
    }

    //filter down the list of items that are selectable based on current filter values
    let selectable = this.getAvailableVariants(filters, property);

    //set all variants to not enabled
    Object.entries(this.variants).forEach(([key, value]) => {
      Object.entries(value).forEach(([key, value]) => {
        value.enabled = false;
      })
    });
    //go through the selectables to enable the available variant options
    selectable.map(element => {
      Object.entries(this.variants).forEach(([key, value]) => {
        const variant = element.variant_data[key];
        if (!variant || !variant.code) return;
        this.variants[key][variant.code].enabled = true;
        if (!variant.dynamicHandle) return;
        this.variants[key][variant.code].dynamicHandle = variant.dynamicHandle;
      });
    })

    const variantData = this.variants[property] !== undefined ? this.variants[property] : false;

    let sortedData = [];
    if (variantData){
      //we need to convert the associative array into an indexed array in order to sort it
      Object.entries(variantData).forEach(([key, value]) => {
        sortedData.push(value);
      })

      sortedData.sort((a,b) =>  {
        if (a.rank < b.rank){
          return -1;
        } else if (a.rank > b.rank){
          return 1;
        } else {
          return 0;
        }
      })
    }

    if (enabledOnly === true){
      sortedData = sortedData.filter(element => {
        return element.enabled === true;
      })
    }

    return sortedData;
  }

  /**
   * @param {array} [variants] - Variants to be sorted.
   * @returns {array} A sorted variant array.
   */
  sortVariants(variants = []) {
    return variants.sort((a,b) =>  {
      const aSizeRank = a.variant_data.size.rank;
      const aConditionRank = a.variant_data.condition.rank
      const bSizeRank = b.variant_data.size.rank;
      const bConditionRank = b.variant_data.condition.rank;

      if (aSizeRank === bSizeRank) {
        // If both size and condition ranks are equal, then omit sorting.
        if (aConditionRank === bConditionRank) {
          return 0;
        } else {
          // If both condition ranks are different, then sort by condition rank.
          return aConditionRank < bConditionRank ? -1 : 1;
        }
      } else {
        // If both size ranks are different, sort by size rank.
        return aSizeRank < bSizeRank ? -1 : 1;
      }
    });
  }

  hasVariantData(property, value){
    return this.variants[property] && this.variants[property][value];
  }

  /**
   *
   * @param {string} text A text to be formatted
   * @returns {string} A formated text lower cased and hyphenated
   */
  hyphenate(text) {
    return text.trim().toLowerCase().replace(/[^a-z0-9 -]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
  }

  setVariantData(property, data) {
    // Ignore unknown properties
    if (!this.properties.includes(property)) return;

    const {
      code,
      display,
      thumbnail,
      rank,
      dynamicHandle,
      staticHandle,
    } = data[property];

    // First instance of a given code value wins.
    if (this.variants[property]?.[code]) return;

    //standardize the incoming variant data
    const variant = {
      value: code,
      label: display,
      thumbnail: thumbnail ?? false,
      rank,
      enabled: true,
    };
    if (dynamicHandle) variant.dynamicHandle = dynamicHandle;
    if (staticHandle) variant.staticHandle = staticHandle;

    this.variants[property] = this.variants[property] || Object.create(null);
    this.variants[property][code] = variant;
  }

  /**
   * Update the current filter and notify the selectors of variant updates
   *
   * @param {*} property - property to update
   * @param {*} value - value to set property in filters to
   * @param {*} redirect - if there are no valid variants based on the filters should redirect to a new color
   */

  setVariantOption(property, value, redirect = true) {
    const currentVariant = this.getCurrentVariant();
    this.setFilter(property, value);
    let selectable = this.getAvailableVariants(this.filters);

    //for color only grab the display name and slugify it to use as the value
    //instead of displaying the default {sku-color} value for colors
    if (property == 'color'){
      let colors = this.getVariantData('color');
      const color = colors.find(element => element.value == value);
      if (color){
        value = this.hyphenate(color.label);
      }
    }

    if (value && value.trim() != "") {
      let url = new URL(window.location);
      url.searchParams.set(property, value);
      window.history.replaceState(null, '', url.toString());
    }

    if (!selectable.length) return this.handleSelectableVariants(property, value, redirect, currentVariant);

    window.dispatchEvent(new CustomEvent('variants-updated', { detail: { property } }));
    window.dispatchEvent(new CustomEvent('price-update'));
  }

  //get the list of variants that are selectable based on the filter and property
  getAvailableVariants(filters, property = false){
    const items = this.data.filter(element => {
      let match = true;
      filters.forEach((filter) => {
        if (property != false && filter.property == property){
          return true;
        } else if (!element.variant_data[filter.property]) {
          match = false;
        } else if (element.variant_data[filter.property].code != filter.value) {
          match = false;
        }
      })
      return match;
    });

    return items;
  }

  // the filters need to be kept in order based on the time there were inserted/updated, LIFO
  setFilter(property, value){
    let index = this.getFilterIndex(property);

    if (index != -1){
      //pop the filter and push it to the end
      this.filters.splice(index,1);
    }
    this.filters.push({property:property, value: value});
  }

  getFilterIndex(property) {
    return this.filters.findIndex(element => {
      return element.property == property;
    });
  }

  getFilter(property){
    let filter = this.filters.find(element => {
      return element.property == property;
    })

    return filter ? filter : false;
  }

  hasVariantOptionSelected(property){
    let filter = this.filters.find(element => {
      return element.property == property;
    })

    return filter ? true : false;
  }

  /**
   * This logic searches for the next best valid variant based on the filters.
   * Start with the current filters and start subtracting the last one.
   * If a variant is found, then redirects to the page of that variant.
   * @param {*} variants - The list of variants.
   * @param {*} variant - The current variant.
   * @returns {void}
   */
  handleVariantsRedirect (variants = [], variant) {
    const filters = Array.from(this.filters);
    let validVariants = Array.from(variants);
    filters.shift();

    let validVariant = variant ? JSON.parse(JSON.stringify(variant)) : null;
    while(filters.length != 0 && validVariants.length == 0){
      validVariants = this.getAvailableVariants(filters);
       if (validVariants.length > 0){
        validVariant = validVariants[0];
        break;
      }
      filters.shift();
    }

    if (!validVariant) return;

    let url = window.location.href.split('/');
    url[url.length-1] = validVariant.handle;
    url = url.join('/');

    const params = new URLSearchParams();
    filters.forEach(filter => {
      params.set(filter.property, filter.value);
    });

    url = `${url}?${params.toString()}`;
    history.replaceState(history.state, '', url);
    window.location.assign(url);
  }

  /**
   * This logic is based on color and the incoming property to find the next best valid option available.
   * @param {*} property - Property to update.
   * @param {*} value - Value to set property in filters to.
   * @param {*} redirect - If there are no valid variants based on the filters should redirect to a new color.
   * @param {*} currentVariant - Current variant before updating the current selection.
   * @returns {void}
   */
  handleSelectableVariants(property, value, redirect, currentVariant) {
    const currentColor = this.getFilter('color').value;

    // Filter the possible valid variants using the same color plus the property that was changed.
    const validVariants = this.data.filter(({ variant_data }) => {
      const propertyCode = variant_data[property].code;
      const colorCode = variant_data.color.code;
      return propertyCode === value && colorCode === currentColor;
    });
    const sortedVariants = this.sortVariants(validVariants);

    // If no valid variants match the same color and changed property, redirect by using all filters and discarding the last until finding the first valid variant.
    if (!sortedVariants.length && redirect) return this.handleVariantsRedirect();

    const [firstValidVariant] = sortedVariants;
    // Update the value of the changed selector and filters in the URL. Since the color requires reloading the page, we can skip it.
    this.properties.forEach((property) => {
      if (property === 'color') return;
      const propertyCode = firstValidVariant.variant_data[property].code;
      this.setFilter(property, propertyCode);
      this.getSelector(property).setValue(propertyCode);
    });

    // For variants with parent options, a valid variant may share the same color, size and condition but differ in the parent option, requiring a redirect.
    if (currentVariant && currentVariant.handle !== firstValidVariant.handle && redirect) return this.handleVariantsRedirect(sortedVariants, firstValidVariant);

    window.dispatchEvent(new CustomEvent('variants-updated', { detail: { property: '*' } }));
    window.dispatchEvent(new CustomEvent('price-update'));
  }
}

class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this.value = undefined; //set the value to be tracked
    this.property = ''; //set the property to be tracked
    window.addEventListener('variants-ready', this.render.bind(this));
    window.addEventListener('variants-updated', this.updateOptions.bind(this));
    window.addEventListener('variant-selected', this.updateSelected.bind(this));
    window.addEventListener('price-update', this.updatePrice.bind(this));
    window.addEventListener('get-variant-properties', () => {
      const event = new CustomEvent('register-variant-property', { detail: { property: this.property, element: this } });
      window.dispatchEvent(event);
    });
  }

  /*
  generate the HTML for the selectors selectable UI elements
  */
  render(event) {

  }

  /*
  update the enabled/disabled state of the selectable UI elements based on the available variants
  */
  updateOptions(event) {

  }

  updatePrice(event) {

    const variant = window.variants.getCurrentVariant();
    if (!variant) return;
    const id = variant.variantId.split("/").slice(-1);
    const productId = variant.productId.split("/").slice(-1);
    fetch(`${window.Shopify.routes.root}variants/${id}.js`)
      .then((response) => {
          if (!response.ok) throw new Error(response)
          return response.json();
        }
      )
      .then((response) => {
        const { price, compare_at_price: compareAtPrice } = response;
        const formattedPrice = formatMoney(price);
        const priceElements = document.querySelectorAll(
          `.product__info-container .product__price #price-item-${productId}`
        );
        priceElements.forEach((el) => {
          el.textContent = formattedPrice;
        });

        //updating msrp
        let adjustedCompareAtPrice = window.variants.product.product_msrp_price ?? 0;
        if (compareAtPrice) adjustedCompareAtPrice = compareAtPrice;
        const compareAtPriceElements = document.querySelectorAll(
          `.product__info-container .product__price #compare-at-price-item-${productId}`
        );

        const formattedCompareAtPrice = formatMoney(adjustedCompareAtPrice);
        const shouldShowCompareAtPrice = adjustedCompareAtPrice > price && compareAtPriceElements.length;

        compareAtPriceElements.forEach((el) => {
          el.textContent = formattedCompareAtPrice
        });
        const compareAtPriceContainers = document.querySelectorAll(
          `.product__info-container .product__price #compare-at-price-container-${productId}`
        );
        compareAtPriceContainers.forEach((el) =>
          el.classList.toggle("hidden", !shouldShowCompareAtPrice)
        );
        const compareAtPriceTooltips = document.querySelectorAll(
          `.product__info-container .product__price #compare-at-price-tooltip-${productId}`
        );
        compareAtPriceTooltips.forEach((el) =>
          el.classList.toggle("hidden", !shouldShowCompareAtPrice)
        );
      })
      .catch((error) => {
        const { price_max, price_min } = window.variants.product;
        const priceElements = document.querySelectorAll(
          `.product__info-container .product__price #price-item-${productId}`
        );
        priceElements.forEach((el) => {
          el.textContent = `${formatMoney(price_min)} - ${formatMoney(price_max)}` ;
        });
      });
  }

  /*
  update the active selected state based on the incoming variant (event.detail.variant)
  */
  updateSelected(event) {

  }

  getProperty(){
    return this.property;
  }

  getValue() {
    return this.value;
  }

  setValue(value) {
    this.value = value;
  }

  getVariants() {
    window.variants.getData(this.property);
  }

  /**
   * Modifies the default behavior of clicking an anchor tag to replace the current entry in the history stack.
   * @param {Event} event - The event object generated by clicking the anchor tag.
   * @returns {void}
   */
  replaceHistoryEntryOnClick(event) {
    history.replaceState(history.state, '', event.currentTarget.href);
  };
}

customElements.define('variant-selector', VariantSelector);